import type { SavedViewRecord } from '@/utils/savedViews';

/**
 * Keeps `?view=<id>` in a list's address in step with the view the list is in.
 * One pure decision, so every case can be tested without a router:
 *
 * - The address names a view the list is not in yet: apply it, once it loads.
 * - That view belongs to another entity: go to that entity's list, same view.
 * - That view does not exist, or is not the user's: say so, drop `view`.
 * - The user picked or cleared a view some other way: update the address.
 */

export type ViewLoad =
    | { state: 'loading' }
    | { state: 'loaded'; view: SavedViewRecord }
    | { state: 'missing' };

export type RoutedViewAction =
    | { kind: 'none' }
    | { kind: 'apply'; view: SavedViewRecord }
    | { kind: 'redirect'; entityId: string; viewId: number }
    | { kind: 'drop-missing' }
    | { kind: 'set-query'; viewId: number }
    | { kind: 'clear-query' };

export interface RoutedViewInput {
    entityId: string;
    /** The `view` in the address, or null. */
    queryViewId: number | null;
    /** The view the list is in now, or null. */
    activeViewId: number | null;
    /** What loading the address's view returned. Ignored without one. */
    load: ViewLoad;
    /** True on the first run after the address changed; false when only the active view changed. */
    addressChanged: boolean;
}

export function parseViewParam(value: unknown): number | null {
    if (typeof value !== 'string' || !/^\d+$/.test(value)) {
        return null;
    }
    return Number(value);
}

export function decideRoutedView(input: RoutedViewInput): RoutedViewAction {
    const { queryViewId, activeViewId, load } = input;

    if (input.addressChanged && queryViewId !== null) {
        if (queryViewId === activeViewId) {
            return { kind: 'none' };
        }
        if (load.state === 'loading') {
            return { kind: 'none' };
        }
        if (load.state === 'missing') {
            return { kind: 'drop-missing' };
        }
        if (load.view.entity !== input.entityId) {
            return { kind: 'redirect', entityId: load.view.entity, viewId: load.view.id };
        }
        return { kind: 'apply', view: load.view };
    }

    if (!input.addressChanged) {
        if (activeViewId === null && queryViewId !== null) {
            return { kind: 'clear-query' };
        }
        if (activeViewId !== null && activeViewId !== queryViewId) {
            return { kind: 'set-query', viewId: activeViewId };
        }
    }
    return { kind: 'none' };
}
